import { Breadcrumb } from 'components/Breadcrumb';
import { PageHeader } from 'components/PageHeader';
import { ITabItem, Tabs } from 'components/tabs/Tabs';
import { TrailerDocumentType } from 'src/types/enums';
import { InternalRole } from '../../auth/endpoints/MeEndpoint.types';
import { useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams, useResolvedPath } from 'react-router-dom';

import { PageHeading } from 'components/PageHeading';
import { useAuth } from 'contexts/auth-context';
import { invariant } from '@utils/invariant';
import { ConfirmDialog } from 'components/dialog/ConfirmDialog';
import { getDisplayError } from '@utils/get-display-error';
import { Pill } from 'components/pill/Pill';
import { TrailerProvider } from './trailerContext';
import { MultiFileUploaderButton } from 'src/app/document/components/MultiFileUploaderButton';
import { captureException } from '@sentry/react';
import { roleToNumber } from 'src/app/auth/roles.client';
import { ErrorState } from 'components/error-state/ErrorState';
import { DelayedSpinner } from 'components/Spinner';
import { deleteTrailer, linkTrailerDocument, unlinkTrailerDocument, useTrailerById, type Trailer } from '../models';

export type { Trailer };

export const TrailerPage = () => {
  const navigate = useNavigate();
  const { me } = useAuth();
  const canDelete = roleToNumber(me.role) >= roleToNumber(InternalRole.InternalAdmin);
  const generalRoute = useResolvedPath('general');
  const costsRoute = useResolvedPath('costs');
  const checksRoute = useResolvedPath('checks');
  const auditLogsRoute = useResolvedPath('audit-logs');
  const tabItems = useMemo<ITabItem[]>(() => {
    const res = [
      {
        title: 'Algemeen',
        path: generalRoute.pathname,
      },
      {
        title: 'Kosten',
        path: costsRoute.pathname,
      },
      {
        title: 'Trailer checks',
        path: checksRoute.pathname,
      },
    ];

    if (roleToNumber(me.role) >= roleToNumber(InternalRole.InternalAdmin)) {
      res.push({
        title: 'Audit logs',
        path: auditLogsRoute.pathname,
      });
    }

    return res;
  }, []);

  const { trailerId } = useParams<{ trailerId: string }>();
  invariant(trailerId);
  const { data, error, isLoading, mutate } = useTrailerById(+trailerId);

  const refreshData = useCallback(() => {
    return mutate();
  }, [mutate]);

  const trailer = data;
  const trailerLabel = trailer ? trailer.name || trailer.licensePlate : '';

  if (isLoading) {
    return <DelayedSpinner size={8} />;
  }

  if (!trailer) {
    return (
      <ErrorState
        error={error}
        title={error ? 'Trailer kon niet worden geladen' : 'Trailer niet gevonden'}
        description={error ? getDisplayError(error) : 'De gevraagde trailer werd niet gevonden.'}
        onRetry={error ? refreshData : undefined}
        variant="page"
      />
    );
  } else {
    return (
      <>
        <PageHeader title={`Trailer ${trailerLabel}`} />

        <TrailerProvider trailer={trailer} refreshData={refreshData}>
          <PageHeading
            leftSide={
              <div className="flex items-center gap-2">
                <Breadcrumb
                  parentItem={{
                    name: 'Trailers',
                    to: '/internal/trailers',
                  }}
                  currentItem={trailerLabel}
                />
                {!trailer.isActive && (
                  <Pill color="negative" size="sm">
                    Inactief
                  </Pill>
                )}
              </div>
            }
            rightSide={
              canDelete ? (
                <div className="flex gap-2">
                  <ConfirmDialog
                    triggerColor="danger"
                    triggerText="Verwijder trailer"
                    title="Trailer verwijderen?"
                    submitText="Verwijder"
                    description={
                      <div>
                        Ben je zeker dat je trailer <strong>{trailerLabel}</strong> permanent wilt verwijderen? Alle
                        checks, kosten en documenten worden ook verwijderd.
                      </div>
                    }
                    onSubmit={async () => {
                      try {
                        await deleteTrailer(trailer.id);
                        toast.success('Trailer verwijderd');
                        navigate('/internal/trailers');
                      } catch (err) {
                        captureException(err);
                        toast.error('Mislukt: ' + getDisplayError(err));
                      }
                    }}
                  />
                </div>
              ) : undefined
            }
          />

          <div className="flex px-4 mb-4 justify-end gap-4">
            <MultiFileUploaderButton
              buttonText="COC"
              title="Gelijkvormigheidsattest (COC)"
              files={trailer.documents.filter((d) => d.documentType === TrailerDocumentType.Coc).map((f) => f.document)}
              onSubmit={async (documents) => {
                let count = 0;
                await Promise.allSettled(
                  documents.map(async (doc) => {
                    try {
                      await linkTrailerDocument(trailer.id, doc.id, TrailerDocumentType.Coc);
                      count += 1;
                      toast.success(`Bestand: ${doc.name}, ${count}/${documents.length} opgeladen`);
                    } catch (err) {
                      captureException(err);
                      toast.error(`Kon bestand ${doc.name} niet opladen: ${getDisplayError(err)}`);
                    }
                  }),
                );
                await refreshData();
              }}
              unlinkDocument={async (docId: string) => {
                try {
                  await unlinkTrailerDocument(trailer.id, docId, TrailerDocumentType.Coc);
                  await refreshData();
                  toast.success('Bestand gewist');
                } catch (err) {
                  captureException(err);
                  toast.error(`Kon bestand niet wissen: ${getDisplayError(err)}`);
                }
              }}
            />
            <MultiFileUploaderButton
              buttonText="Identificatie"
              title="Identificatieverslag"
              files={trailer.documents
                .filter((d) => d.documentType === TrailerDocumentType.Identification)
                .map((f) => f.document)}
              onSubmit={async (documents) => {
                let count = 0;
                await Promise.allSettled(
                  documents.map(async (doc) => {
                    try {
                      await linkTrailerDocument(trailer.id, doc.id, TrailerDocumentType.Identification);
                      count += 1;
                      toast.success(`Bestand: ${doc.name}, ${count}/${documents.length} opgeladen`);
                    } catch (err) {
                      captureException(err);
                      toast.error(`Kon bestand ${doc.name} niet opladen: ${getDisplayError(err)}`);
                    }
                  }),
                );
                await refreshData();
              }}
              unlinkDocument={async (docId: string) => {
                try {
                  await unlinkTrailerDocument(trailer.id, docId, TrailerDocumentType.Identification);
                  await refreshData();
                  toast.success('Bestand gewist');
                } catch (err) {
                  captureException(err);
                  toast.error(`Kon bestand niet wissen: ${getDisplayError(err)}`);
                }
              }}
            />
            <MultiFileUploaderButton
              buttonText="Kenteken"
              title="Kentekenbewijs"
              files={trailer.documents
                .filter((d) => d.documentType === TrailerDocumentType.Registration)
                .map((f) => f.document)}
              onSubmit={async (documents) => {
                let count = 0;
                await Promise.allSettled(
                  documents.map(async (doc) => {
                    try {
                      await linkTrailerDocument(trailer.id, doc.id, TrailerDocumentType.Registration);
                      count += 1;
                      toast.success(`Bestand: ${doc.name}, ${count}/${documents.length} opgeladen`);
                    } catch (err) {
                      captureException(err);
                      toast.error(`Kon bestand ${doc.name} niet opladen: ${getDisplayError(err)}`);
                    }
                  }),
                );
                await refreshData();
              }}
              unlinkDocument={async (docId: string) => {
                try {
                  await unlinkTrailerDocument(trailer.id, docId, TrailerDocumentType.Registration);
                  await refreshData();
                  toast.success('Bestand gewist');
                } catch (err) {
                  captureException(err);
                  toast.error(`Kon bestand niet wissen: ${getDisplayError(err)}`);
                }
              }}
            />
            <MultiFileUploaderButton
              buttonText="Keuring"
              title="Keuringsbewijs"
              files={trailer.documents
                .filter((d) => d.documentType === TrailerDocumentType.Inspection)
                .map((f) => f.document)}
              onSubmit={async (documents) => {
                let count = 0;
                await Promise.allSettled(
                  documents.map(async (doc) => {
                    try {
                      await linkTrailerDocument(trailer.id, doc.id, TrailerDocumentType.Inspection);
                      count += 1;
                      toast.success(`Bestand: ${doc.name}, ${count}/${documents.length} opgeladen`);
                    } catch (err) {
                      captureException(err);
                      toast.error(`Kon bestand ${doc.name} niet opladen: ${getDisplayError(err)}`);
                    }
                  }),
                );
                await refreshData();
              }}
              unlinkDocument={async (docId: string) => {
                try {
                  await unlinkTrailerDocument(trailer.id, docId, TrailerDocumentType.Inspection);
                  await refreshData();
                  toast.success('Bestand gewist');
                } catch (err) {
                  captureException(err);
                  toast.error(`Kon bestand niet wissen: ${getDisplayError(err)}`);
                }
              }}
            />
            <MultiFileUploaderButton
              buttonText="Verzekering"
              title="Verzekeringsbewijs"
              files={trailer.documents
                .filter((d) => d.documentType === TrailerDocumentType.Insurance)
                .map((f) => f.document)}
              onSubmit={async (documents) => {
                let count = 0;
                await Promise.allSettled(
                  documents.map(async (doc) => {
                    try {
                      await linkTrailerDocument(trailer.id, doc.id, TrailerDocumentType.Insurance);
                      count += 1;
                      toast.success(`Bestand: ${doc.name}, ${count}/${documents.length} opgeladen`);
                    } catch (err) {
                      captureException(err);
                      toast.error(`Kon bestand ${doc.name} niet opladen: ${getDisplayError(err)}`);
                    }
                  }),
                );
                await refreshData();
              }}
              unlinkDocument={async (docId: string) => {
                try {
                  await unlinkTrailerDocument(trailer.id, docId, TrailerDocumentType.Insurance);
                  await refreshData();
                  toast.success('Bestand gewist');
                } catch (err) {
                  captureException(err);
                  toast.error(`Kon bestand niet wissen: ${getDisplayError(err)}`);
                }
              }}
            />
          </div>

          <div className="px-4">
            <Tabs items={tabItems} />
          </div>
        </TrailerProvider>
      </>
    );
  }
};
